/**
 * AG · Gift card — behaviour for sections/ag-gift-card.liquid.
 *
 * Everything is client-side: the amounts are plain variants, so picking one only
 * swaps the hidden `id` input and the amount printed on the card. No Section
 * Rendering round-trip is needed because each button already carries its price
 * formatted by Liquid, which keeps it market-aware.
 */

import { StandardEvents } from '@shopify/events';

class AgGiftCardComponent extends HTMLElement {
  #controller = new AbortController();
  #addedTimer = 0;

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    // Shopify schedules "Send on" against the buyer's timezone, not the shop's.
    const offset = this.querySelector('[data-ag-gc-offset]');
    if (offset instanceof HTMLInputElement) offset.value = String(new Date().getTimezoneOffset());

    // Never let anyone schedule a gift into the past.
    const sendOn = this.querySelector('[data-ag-gc-send-on]');
    if (sendOn instanceof HTMLInputElement) sendOn.min = new Date().toISOString().slice(0, 10);
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#addedTimer);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-ag-gc-amount]') : null;
    if (!(target instanceof HTMLButtonElement) || target.disabled) return;
    this.#selectAmount(target);
  };

  /** @param {HTMLButtonElement} button */
  #selectAmount(button) {
    const id = button.dataset.agGcAmount;
    if (!id) return;

    this.querySelectorAll('[data-ag-gc-amount]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const on = el === button;
      el.classList.toggle('is-on', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    const input = this.querySelector('[data-ag-gc-variant]');
    if (input instanceof HTMLInputElement) input.value = id;

    const value = this.querySelector('[data-ag-gc-card-value]');
    if (value instanceof HTMLElement && button.dataset.agGcPrice) {
      value.textContent = button.dataset.agGcPrice;
    }

    // Keep the shareable URL honest about which amount is selected.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState({}, '', url);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-ag-gc-delivery]')) return;

    const wrap = this.querySelector('[data-ag-gc-recipient]');
    const fields = this.querySelector('[data-ag-gc-recipient-fields]');
    const wanted = target.value === 'recipient';

    if (wrap instanceof HTMLElement) wrap.hidden = !wanted;
    // Disabled fields are not submitted, so an unused recipient block never
    // reaches Shopify as a set of empty line item properties.
    if (fields instanceof HTMLFieldSetElement) fields.disabled = !wanted;

    if (wanted) {
      const email = this.querySelector('input[name="properties[Recipient email]"]');
      if (email instanceof HTMLInputElement) email.focus();
    }
  };

  /** @param {'adding' | 'added' | ''} state */
  #setCtaState(state) {
    const label = this.dataset.addedLabel || 'Added to cart ✓';
    const button = this.querySelector('[data-ag-gc-cta]');
    if (!(button instanceof HTMLElement)) return;

    if (state) button.dataset.state = state;
    else delete button.dataset.state;

    const text = button.querySelector('[data-ag-gc-cta-label]');
    if (!(text instanceof HTMLElement)) return;

    if (state === 'added') {
      text.dataset.original = text.dataset.original ?? text.textContent ?? '';
      text.textContent = label;
    } else if (!state && text.dataset.original !== undefined) {
      text.textContent = text.dataset.original;
      delete text.dataset.original;
    }
  }

  #onSubmit = () => {
    clearTimeout(this.#addedTimer);
    this.#setCtaState('adding');
  };

  /** @param {Event & {detail?: any, promise?: Promise<any>}} event */
  #onCartUpdate = (event) => {
    const source = event.target instanceof HTMLElement ? event.target : null;
    if (source && !this.contains(source)) return;

    const promise = event.promise ?? event.detail?.promise;
    Promise.resolve(promise)
      .then((result) => {
        const failed = result && typeof result === 'object' && 'detail' in result && result.detail?.didError;
        const count = result?.cart?.totalQuantity;
        if (typeof count === 'number') {
          document.querySelectorAll('[data-ag-cart-count]').forEach((el) => { el.textContent = String(count); el.toggleAttribute('data-empty', count === 0); });
        }
        if (failed) return this.#setCtaState('');
        this.#setCtaState('added');
        this.#addedTimer = window.setTimeout(() => this.#setCtaState(''), 1800);
      })
      .catch(() => this.#setCtaState(''));
  };
}

if (!customElements.get('ag-gift-card-component')) {
  customElements.define('ag-gift-card-component', AgGiftCardComponent);
}
